import { eq, useLiveQuery } from '@tanstack/react-db'
import { activeLogs } from '@/db/application/callouts'
import { applyAddendums, orderAddendums } from '@/db/application/addendums'

import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import type { UpdateCalloutInput } from '@/db/domain/models'
import { useDb } from '@/lib/dbContext'
import type { Addendum, AddendumChange, Callout, Customer, Note, StatusChangeLog } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import type { InvoiceServiceScreenProps } from '@/screens/InvoiceServiceScreen'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type InvoiceState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: InvoiceServiceScreenProps }

function logsToNotes(
  logs: Note[] | undefined,
  statusChanges: StatusChangeLog[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
): CalloutNotesSectionNote[] {
  // Soft-deleted notes are no longer part of the record; the audit trail for
  // them lives on the Log Work feed.
  const noteItems: CalloutNotesSectionNote[] = activeLogs(logs ?? []).map((log) =>
    log.photoUrl
      ? {
          id: log.id,
          kind: 'photo',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          photoUrl: log.photoUrl ?? '',
          photoUrls: log.photoUrls,
          caption: log.caption ?? '',
        }
      : {
          id: log.id,
          kind: 'text',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          text: log.text ?? '',
        },
  )

  const statusItems: CalloutNotesSectionNote[] = (statusChanges ?? []).map((change) => ({
    id: change.id,
    kind: 'status_change',
    authorName: getAuthorDisplayName(change.authorId),
    createdAt: change.createdAt,
    previousStatus: change.previousStatus,
    newStatus: change.newStatus,
    reason: change.reason,
  }))

  return [...noteItems, ...statusItems].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )
}

export function useCalloutInvoiceService(
  serviceIdentity: string,
  onDone: () => void,
): InvoiceState {
  const db = useDb()
  const tryPublicId = looksLikePublicId(serviceIdentity)

  const calloutByIdResult = useLiveQuery(
    (q) =>
      serviceIdentity
        ? q
            .from({ callouts: db.calloutsCollection })
            .where(({ callouts }) => eq(callouts.id, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity],
  )

  const calloutByPublicIdResult = useLiveQuery(
    (q) =>
      serviceIdentity && tryPublicId
        ? q
            .from({ callouts: db.calloutsCollection })
            .where(({ callouts }) => eq(callouts.publicId, serviceIdentity))
            .findOne()
        : null,
    [serviceIdentity, tryPublicId],
  )

  const callout =
    (calloutByIdResult.data as Callout | undefined) ??
    (calloutByPublicIdResult.data as Callout | undefined)
  const serviceId = callout?.id ?? serviceIdentity
  if (!tryPublicId && callout && (calloutByIdResult.data as Callout | undefined)) {
    captureLegacyIdRoute('callout', serviceIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!callout?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, callout.customerId))
        .findOne()
    },
    [callout?.customerId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const notesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ notes: db.notesCollection })
            .where(({ notes }) => eq(notes.serviceId, serviceId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [serviceId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ statusChanges: db.statusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, serviceId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [serviceId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      serviceId
        ? q
            .from({ addendums: db.addendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, serviceId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [serviceId],
  )

  // Archived (Invoiced/Cancelled) services aren't in the live sync; pull one
  // in on demand so Invoice Prep loads from a direct link too.
  const recordQueriesSettled =
    !calloutByIdResult.isLoading &&
    !calloutByPublicIdResult.isLoading &&
    !calloutByIdResult.isError &&
    !calloutByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'callout',
    serviceIdentity,
    recordQueriesSettled && !callout,
  )

  if (
    hydrating ||
    calloutByIdResult.isLoading ||
    calloutByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    addendumsResult.isLoading
  ) {
    return { kind: 'loading' }
  }

  if (
    calloutByIdResult.isError ||
    calloutByPublicIdResult.isError ||
    customerResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    addendumsResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!callout) return { kind: 'not_found' }

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )
  // The locked record keeps its values; addendums layer on top to give what is
  // in force now — including a corrected Omni invoice ID.
  const effective = applyAddendums(
    callout as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as typeof callout

  const handleSaveAddendum = async (reason: string, changes: AddendumChange[]) => {
    await db.appendAddendumAndWait(callout.id, { reason, changes })
  }

  const customer = customerResult.data as Customer | undefined
  const linkedCustomerName = customer
    ? customer.externalId
      ? `${customer.fullName} – ${customer.externalId}`
      : customer.fullName
    : callout.contactName ?? ''

  const notes = logsToNotes(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleMarkInvoiced = async (params: {
    omniInvoiceId?: string
    noOmniInvoice: boolean
    billingOutcome?: 'OmniInvoice' | 'NoOmniInvoice' | 'PosPayment'
  }) => {
    const patch: UpdateCalloutInput = { status: 'Invoiced' }

    if (!params.noOmniInvoice && params.omniInvoiceId) {
      patch.invoiceId = params.omniInvoiceId
    }
    if (params.noOmniInvoice) {
      patch.invoiceId = undefined
    }

    await withSpan(
      {
        op: 'callout.status.change',
        name: 'Mark callout invoiced',
        attributes: { serviceId: callout.id, nextStatus: 'Invoiced' },
      },
      async () => {
        await db.updateCalloutAndWait(callout.id, patch)
        onDone()
      },
    )
  }

  const handleMoveToReview = async (reason: string) => {
    const normalizedReason = reason.trim()
    if (!normalizedReason) throw new Error('Reason is required when moving a callout to review')
    return withSpan(
      {
        op: 'callout.status.change',
        name: 'Move callout to review from invoice prep',
        attributes: { serviceId: callout.id, nextStatus: 'Review' },
      },
      () => db.updateCalloutAndWait(callout.id, { status: 'Review', statusReason: normalizedReason }),
    )
  }

  const handleMoveToBooked = async () =>
    withSpan(
      {
        op: 'callout.status.change',
        name: 'Move callout to booked from invoice prep',
        attributes: { serviceId: callout.id, nextStatus: 'Booked' },
      },
      () => db.updateCalloutAndWait(callout.id, { status: 'Booked' }),
    )

  return {
    kind: 'ready',
    screenProps: {
      serviceType: 'Callout',
      resourceLabel: 'Callout',
      publicId: callout.publicId,
      customerName: linkedCustomerName,
      customerId: callout.customerId,
      customerPublicId: customer?.publicId,
      visitAddress: effective.contactAddress,
      siteContact: effective.contactName,
      contactPhone: effective.contactPhone,
      contactEmail: effective.contactEmail,
      invoiceTo: effective.invoiceName || effective.contactName || '',
      invoiceEmail: effective.invoiceEmail,
      invoicePhone: effective.invoicePhone,
      invoiceAddress: effective.invoiceAddress,
      serviceInvoice: {
        invoiceName: effective.invoiceName,
        invoiceEmail: effective.invoiceEmail,
        invoicePhone: effective.invoicePhone,
        invoiceAddress: effective.invoiceAddress,
        invoiceVat: effective.invoiceVat,
      },
      customerInvoice: customer
        ? {
            invoiceName: customer.invoiceName,
            invoiceEmail: customer.invoiceEmail,
            invoicePhone: customer.invoicePhone,
            invoiceAddress: customer.invoiceAddress,
            invoiceVat: customer.invoiceVat,
          }
        : undefined,
      invoiceVat: effective.invoiceVat,
      issueSummary: effective.issueSummary ?? '',
      scheduledAt: callout.isWholeDay
        ? `${formatRelativeDateTime(callout.startTime, { includeTime: false })} · No Time`
        : formatRelativeDateTime(callout.startTime),
      calloutStatus: callout.status,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      omniCustomerId: customer?.externalId,
      omniInvoiceId: effective.invoiceId,
      notes,
      onMarkInvoiced: handleMarkInvoiced,
      onMoveToReview: handleMoveToReview,
      onMoveToBooked: handleMoveToBooked,
      lockedStatus:
        callout.status === 'Invoiced'
          ? 'Invoiced'
          : callout.status === 'Cancelled'
            ? 'Cancelled'
            : undefined,
      addendums,
      onSaveAddendum: handleSaveAddendum,
    },
  }
}
